'use client';

import './gift-catalog-card.scss';

import Link from 'next/link';
import { useTranslation } from 'react-i18next';

import { Badge } from '@sola/ark-core/atoms/badge/badge.js';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.js';
import { FundPromotionsEnum } from '@sola/contentful-model/lib/taxonomy-values.js';
import { BadgeColors } from '@sola/schema/lib/schema.js';
import { SearchResultProps } from '@sola/skippy-search-sdk/lib/schema.js';
import { titleCase } from '@sola/utils/string.js';

import { Image } from '../../atoms/image/image.js';
import { getBaseSizeProps } from '../../utils/text-utils';
import { FeatureBase } from '../feature-base/feature-base';

export interface GiftCatalogCardProps extends SearchResultProps {
  seoLevel?: number;
  variant?: 'default' | 'grid';
  onClick?: () => void;
}

export const GiftCatalogCard = ({
  seoLevel = 3,
  title,
  summary,
  path = '#',
  media,
  taxonomyFundPromotion,
  fund,
  variant = 'default',
  onClick,
}: GiftCatalogCardProps) => {
  const { t } = useTranslation();

  const Comp = Link;
  const CompProps = {
    href: path,
    target: '_self',
    onClick,
  };

  const promotionColorMap: Partial<Record<FundPromotionsEnum, BadgeColors>> = {
    [FundPromotionsEnum.enum.mostNeeded]: BadgeColors.enum.warning,
    [FundPromotionsEnum.enum.popular]: BadgeColors.enum.neutral,
    [FundPromotionsEnum.enum.urgent]: BadgeColors.enum.negative,
  };

  const getPromotionBadgeColor = (promotion: string): BadgeColors =>
    promotionColorMap[promotion as FundPromotionsEnum] ??
    BadgeColors.enum.neutral;

  return (
    <Comp
      {...CompProps}
      className="GiftCatalogCard flex flex-col"
      onClick={onClick}
    >
      <div className="GiftCatalogCard__media relative">
        <Image
          image={media?.image}
          lazyLoad="eager"
          className="h-full"
          aspectRatio={variant === 'grid' ? '4-3' : '16-9'}
          hasPlaceholder
        />
        {taxonomyFundPromotion && taxonomyFundPromotion.length > 0 && (
          <div className="absolute flex flex-wrap top-comfy-xs left-comfy-xs right-snug-xl gap-snug-md">
            {taxonomyFundPromotion.map((promotion) => {
              if (promotion !== FundPromotionsEnum.enum.giftCatalog) {
                return (
                  <Badge
                    key={promotion}
                    label={titleCase(promotion)}
                    color={getPromotionBadgeColor(promotion)}
                    size="sm"
                  />
                );
              }
              return null;
            })}
          </div>
        )}
      </div>

      <div className="GiftCatalogCard__content flex-1 flex flex-col justify-between">
        <FeatureBase
          seoLevel={seoLevel}
          {...getBaseSizeProps(4)}
          title={title}
          body={<p>{summary}</p>}
          className="px-comfy-xl py-comfy-lg"
        />
        {fund?.catalogAmount && (
          <div className="GiftCatalogCard__footer w-full label-mild label-xl px-comfy-xl py-comfy-lg">
            {t('checkout.currency.usd', { amount: fund.catalogAmount })}
          </div>
        )}
      </div>
    </Comp>
  );
};

export const GiftCatalogCardSkeleton: React.FC = () => {
  return (
    <div className="GiftCatalogCard flex flex-col">
      <div className="GiftCatalogCard__media relative">
        <Skeleton
          width="100%"
          height="auto"
          style={{ aspectRatio: '4/3' }}
          borderRadius="0.5rem"
          className="h-full"
        />
        <div className="absolute top-comfy-xs left-comfy-xs">
          <Skeleton height={24} width={60} borderRadius="9999px" />
        </div>
      </div>

      <div className="GiftCatalogCard__content flex-1 flex flex-col justify-between">
        <div className="px-comfy-xl py-comfy-lg flex flex-col gap-comfy-sm">
          <div className="flex flex-col gap-snug-xs">
            <Skeleton height={28} width="95%" />
            <Skeleton height={28} width="75%" />
          </div>
          <div className="flex flex-col gap-snug-xs">
            <Skeleton height={20} width="100%" />
            <Skeleton height={20} width="90%" />
          </div>
        </div>
        <div className="GiftCatalogCard__footer w-full px-comfy-xl py-comfy-lg">
          <Skeleton height={28} width="50%" />
        </div>
      </div>
    </div>
  );
};
